import { useRole } from "@4uhub/lib4uhub";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import { Box, Card, IconButton, Tooltip, Typography } from "@mui/material";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { IDashBoardPanel } from "../../../../../../models/dashboardpanel";
import { useDashboardView } from "../../../../../../store/contexts/DashBoardViewContext";
import { DashboardCardPopover } from "./DashboardCardPopover/DashboardCardPopover";

interface IDashboardCardProps {
  dashboard: IDashBoardPanel;
}

const TRANSLATION_PATH = "page.panels.";

export const DashboardCard = memo(
  ({ dashboard: { id, name } }: IDashboardCardProps) => {
    const { isView } = useDashboardView();

    const { t } = useTranslation();

    const ref = useRef<HTMLDivElement>(null);

    const textRef = useRef<HTMLDivElement>(null);

    const [width, setWidth] = useState(100);

    const [isTruncated, setIsTruncated] = useState(false);

    const [openModal, setOpenModal] = useState(false);

    const [hover, setHover] = useState(false);

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const hasDeletePermission = useRole(["Delete"]);

    const hasUpdatePermission = useRole(["Update"]);

    const navigate = useNavigate();

    const mouseEnterHandler = useCallback(() => {
      setHover(true);
    }, []);

    const mouseLeaveHandler = useCallback(() => {
      setHover(false);
    }, []);

    const handleCardClick = () => {
      navigate(`panel/${id}`);
    };

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const openModalHandler = useCallback(() => {
      setOpenModal(true);
    }, []);

    const closeModalHandler = useCallback(() => {
      setAnchorEl(null);
      setOpenModal(false);
    }, []);

    const open = Boolean(anchorEl);

    const popoverId = open ? "panel-grid-popover" : undefined;

    const showActions =
      (hasDeletePermission || hasUpdatePermission) &&
      (hover || open) &&
      !isView;

    console.log("Current width:", width);

    useLayoutEffect(() => {
      const element = ref.current;
      if (!element) return;

      const resizeObserver = new ResizeObserver(() => {
        setWidth(element.offsetWidth);
      });

      resizeObserver.observe(element);

      return () => {
        resizeObserver.disconnect();
      };
    }, [ref]);

    useLayoutEffect(() => {
      const element = textRef.current;
      if (element) {
        setIsTruncated(element.scrollWidth > element.offsetWidth);
      }
    }, [name, width]);

    return (
      <>
        <DashboardCardPopover
          id={id}
          openModal={openModal}
          anchorEl={anchorEl}
          handleClose={handleClose}
          closeModalHandler={closeModalHandler}
          openModalHandler={openModalHandler}
        />

        <Card
          elevation={0}
          sx={(t) => ({
            p: 2,
            height: 70,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            border: `1px solid ${t.palette.divider}`,
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            backgroundColor:
              t.palette.mode === "dark" ? t.palette.grey[900] : "transparent",
            "&:hover": {
              border: `1px solid ${t.palette.primary.main}`,
              boxShadow: "0px 0px 20px -12px rgba(0,0,0,0.5)",
            },
          })}
          onMouseEnter={mouseEnterHandler}
          onMouseLeave={mouseLeaveHandler}
          onClick={handleCardClick}
          ref={ref}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <DashboardRoundedIcon sx={{ fontSize: 30 }} />
            <Tooltip
              title={name}
              disableFocusListener={!isTruncated}
              disableHoverListener={!isTruncated}
            >
              <Typography
                sx={{
                  maxWidth: `calc(${width}px - 100px)`,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                ref={textRef}
              >
                {name}
              </Typography>
            </Tooltip>
          </Box>
          {showActions && (
            <IconButton
              size="small"
              aria-describedby={popoverId}
              onClick={handleClick}
            >
              <MoreVertRoundedIcon fontSize="small" color="action" />
            </IconButton>
          )}
        </Card>
      </>
    );
  }
);
